import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import usePageMeta from "../utils/usePageMeta";

const Section = ({ title, children }) => (
    <section className="mb-10">
        <h2 className="font-display text-xl font-bold text-ds-text mb-3">{title}</h2>
        <div className="font-sans text-sm text-text-muted leading-relaxed space-y-3">
            {children}
        </div>
    </section>
);

const MethodologyPage = () => {
    usePageMeta({
        title: "Methodology — Doritostats",
        description: "How Doritostats calculates power rankings, KTC roster value, and other analytics.",
    });

    return (
        <div className="min-h-screen bg-bg">
            <Header />
            <div className="bg-forest border-b-4 border-mustard">
                <div className="max-w-3xl mx-auto px-6 py-8">
                    <p className="text-mustard text-xs font-sans font-bold uppercase tracking-widest mb-2">
                        How it works
                    </p>
                    <h1 className="font-display text-4xl font-black text-white leading-none">
                        Methodology
                    </h1>
                </div>
            </div>

            <main className="max-w-3xl mx-auto px-6 py-10">

                {/* ── KTC Roster Value ── */}
                <Section title="KTC Roster Value">
                    <p>
                        <strong className="text-ds-text">KeepTradeCut (KTC)</strong> is a community-driven
                        platform that publishes weekly trade values for fantasy football players. Values run
                        from 0 to 9999, where higher means more valuable. You can explore the full rankings
                        at{" "}
                        <a
                            href="https://keeptradecut.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-forest underline underline-offset-2 hover:text-forest/80 transition-colors"
                        >
                            keeptradecut.com
                        </a>
                        .
                    </p>
                    <p>
                        Doritostats scrapes KTC rankings every <strong className="text-ds-text">Tuesday at 6:00am ET</strong>,
                        after Monday Night Football scores have settled. The data shown in the Power Rankings
                        tab reflects the most recently scraped week; if the current week hasn't been scraped
                        yet, the prior week's data is used and a notice is displayed.
                    </p>

                    <h3 className="font-sans font-bold text-ds-text pt-2">Score formula</h3>
                    <p>
                        A team's <em>KTC Roster Score</em> is calculated as:
                    </p>
                    <div className="bg-surface border border-border rounded-lg px-5 py-4 font-mono text-xs text-ds-text">
                        KTC Roster Score = Σ(starter KTC values) + top-2 bench players' KTC values
                    </div>
                    <ul className="list-disc list-outside ml-5 space-y-1">
                        <li>
                            <strong className="text-ds-text">Starters</strong> are all players in active
                            lineup slots (QB, RB, WR, TE, FLEX, K, DST, etc. — everything except Bench and IR).
                        </li>
                        <li>
                            <strong className="text-ds-text">Top-2 bench</strong> adds the two highest-valued
                            players sitting on the bench, reflecting the depth of a roster beyond the starting lineup.
                        </li>
                        <li>
                            Players with no KTC match contribute 0 to the score. A{" "}
                            <span className="inline-flex items-center gap-0.5">
                                <span className="font-sans font-bold text-text-muted">ⓘ</span>
                            </span>
                            {" "}icon on the Power Rankings table lists any unmatched players for that team.
                        </li>
                    </ul>

                    <h3 className="font-sans font-bold text-ds-text pt-2">Redraft vs. dynasty values</h3>
                    <p>
                        KTC publishes separate rankings for <strong className="text-ds-text">redraft</strong>{" "}
                        (single-season) and <strong className="text-ds-text">dynasty</strong> (multi-year)
                        leagues. Doritostats automatically selects the appropriate value based on your league type:
                    </p>
                    <ul className="list-disc list-outside ml-5 space-y-1">
                        <li><strong className="text-ds-text">Redraft leagues</strong> → redraft values</li>
                        <li><strong className="text-ds-text">Dynasty leagues</strong> → dynasty values</li>
                        <li><strong className="text-ds-text">Keeper leagues</strong> → dynasty values (long-term player value matters more)</li>
                    </ul>

                    <h3 className="font-sans font-bold text-ds-text pt-2">Player name matching</h3>
                    <p>
                        Player names from your fantasy provider (ESPN, Sleeper, Yahoo) are matched against
                        KTC's names using a combination of exact matching and fuzzy string comparison
                        (via <code className="bg-surface px-1 rounded text-xs">rapidfuzz</code>). Names are
                        normalized before matching: accents are stripped, suffixes like Jr./Sr./III are
                        removed, and known aliases (e.g., "Hollywood Brown" ↔ "Marquise Brown") are resolved
                        automatically.
                    </p>
                    <p>
                        If a player can't be matched with sufficient confidence, they are excluded from the
                        score and listed in the unmatched tooltip. This list is a useful signal for catching
                        stale aliases — if you notice a player repeatedly showing as unmatched, please{" "}
                        <Link
                            to="/contact/"
                            className="text-forest underline underline-offset-2 hover:text-forest/80 transition-colors"
                        >
                            let us know
                        </Link>
                        .
                    </p>
                </Section>

                {/* ── Power Score ── */}
                <Section title="Power Rankings Score">
                    <p>
                        The <strong className="text-ds-text">Power Score</strong> is a composite performance
                        metric computed each week. It blends a team's recent scoring output with their
                        historical performance to produce a single comparable number — higher is better.
                    </p>
                    <p>
                        Power scores are recalculated each week and reflect cumulative season performance
                        through the selected week.
                    </p>
                </Section>

                {/* ── Luck Index ── */}
                <Section title="Luck Index">
                    <p>
                        The <strong className="text-ds-text">Luck Index</strong> measures how much a team's
                        win-loss record has been influenced by the randomness of their schedule. A team that
                        scores highly every week but keeps facing the top scorer is "unlucky"; a team with a
                        mediocre score that keeps beating the bottom scorer is "lucky."
                    </p>
                    <p>
                        Each week, a team's score is compared against every other team's score. The luck
                        index accumulates these comparisons over the season to surface systematic over- or
                        under-performance relative to what a random schedule would have produced.
                    </p>
                </Section>

                <p className="text-xs text-text-muted font-sans mt-6">
                    Have a question or found an issue?{" "}
                    <Link to="/contact/" className="text-forest underline underline-offset-2 hover:text-forest/80 transition-colors">
                        Contact us
                    </Link>
                    .
                </p>
            </main>
        </div>
    );
};

export default MethodologyPage;
